import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { transactionAPI } from '../services/api';
import { BarChart3, PieChart, TrendingUp, DollarSign, Calendar, Filter } from 'lucide-react';

const CostStatistics = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  // 비용 통계 조회
  const { data: statistics, isLoading } = useQuery({
    queryKey: ['costStatistics', startDate, endDate, selectedPeriod],
    queryFn: () => transactionAPI.getCostStatistics({
      startDate,
      endDate,
      period: selectedPeriod
    }),
    enabled: !!startDate && !!endDate && user?.role === 'admin'
  });

  // 묘지별 통계 조회
  const { data: cemeteryStats } = useQuery({
    queryKey: ['cemeteryCostStatistics', startDate, endDate],
    queryFn: () => transactionAPI.getCemeteryCostStatistics(user?.cemetery_id, {
      startDate,
      endDate
    }),
    enabled: !!startDate && !!endDate && user?.role === 'admin'
  });

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const today = new Date();
    let start, end;
    
    switch (period) {
      case 'week':
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num || 0);
  };

  // 권한 체크 (Hook 호출 이후)
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-red-800">접근 권한이 없습니다</h2>
          <p className="text-red-600 mt-2">비용 통계는 관리자만 접근할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">비용 통계</h1>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BarChart3 size={20} />
          <span>관리자 전용</span>
        </div>
      </div>

      {/* 기간 선택 */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-500" />
            <span className="font-medium text-gray-700">기간 선택</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePeriodChange('week')}
              className={`btn ${selectedPeriod === 'week' ? 'btn-primary' : 'btn-secondary'}`}
            >
              주간
            </button>
            <button
              onClick={() => handlePeriodChange('month')}
              className={`btn ${selectedPeriod === 'month' ? 'btn-primary' : 'btn-secondary'}`}
            >
              월간
            </button>
            <button
              onClick={() => handlePeriodChange('quarter')}
              className={`btn ${selectedPeriod === 'quarter' ? 'btn-primary' : 'btn-secondary'}`}
            >
              분기
            </button>
            <button
              onClick={() => handlePeriodChange('year')}
              className={`btn ${selectedPeriod === 'year' ? 'btn-primary' : 'btn-secondary'}`}
            >
              연간
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input"
            />
            <span className="text-gray-500">~</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">통계를 불러오는 중...</div>
      ) : statistics ? (
        <>
          {/* 전체 통계 카드 */}
          <div className="grid grid-3 mb-6" style={{ gap: '20px' }}>
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">전체 매출</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(statistics.total_revenue)}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <DollarSign className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">계약 건수</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(statistics.contract_count)}건
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <BarChart3 className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">평균 계약 금액</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(statistics.average_contract_value)}
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <TrendingUp className="text-yellow-600" size={24} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">묘 형태별 매출</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(statistics.tomb_type_count)}종
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <PieChart className="text-purple-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* 묘지별 통계 */}
          {cemeteryStats && Array.isArray(cemeteryStats) && cemeteryStats.length > 0 && (
            <div className="card mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">묘지별 통계</h3>
              <div className="overflow-x-auto">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th>묘지명</th>
                      <th>계약 건수</th>
                      <th>총 매출</th>
                      <th>평균 계약 금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cemeteryStats.map((stat) => (
                      <tr key={stat.cemetery_id}>
                        <td className="font-medium">{stat.cemetery_name}</td>
                        <td>{formatNumber(stat.contract_count)}건</td>
                        <td>{formatCurrency(stat.total_revenue)}</td>
                        <td>{formatCurrency(stat.average_contract_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 비용 항목별 통계 */}
          {statistics.cost_item_statistics && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">비용 항목별 통계</h3>
              <div className="space-y-3">
                {Object.entries(statistics.cost_item_statistics).map(([item, amount]) => (
                  <div key={item} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{item}</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <div className="text-gray-500">기간을 선택하여 통계를 확인하세요.</div>
        </div>
      )}
    </div>
  );
};

export default CostStatistics; 